import React, { useState, useEffect } from "react";
import "./importAndExport.css";
import { useDispatch } from "react-redux";
import CountryCodePrompt from "../components/CountryCodePrompt";
import * as XLSX from "xlsx";
import {
  clearContactsCache,
  saveBulkContacts,
} from "../../../core/data/redux/slices/ContactSlice";
import api from "../../../core/axios/axiosInstance";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { addTag, fetchTags } from "../../../core/data/redux/slices/TagSlice";
import { useSelector } from "react-redux";

const ImportAndExport = () => {
  const dispatch = useDispatch();
  const [isImporting, setIsImporting] = useState(false);
  const [showCountryCodePrompt, setShowCountryCodePrompt] = useState(false);
  const [pendingImportProvider, setPendingImportProvider] = useState(null);

  const { tags } = useSelector((state) => state.tags)

  useEffect(() => {
    dispatch(fetchTags())
  }, [])

  const options = [
    {
      id: "csvExcel",
      label: "Import contacts from CSV / Excel File",
      icon: "/assets/img/customIcons/excelLogo.png",
    },
    {
      id: "google",
      label: "Import contacts from Google",
      icon: "/assets/img/icons/googleLogo.png",
    },
    {
      id: "hubspot",
      label: "Import contacts from Hubspot",
      icon: "/assets/img/icons/hubspotLogo.jpg",
    },
    {
      id: "zoho",
      label: "Import contacts from Zoho",
      icon: "/assets/img/customIcons/zohoLogo.png",
    },
  ];

  // File selection and import is handled inside <CountryCodePrompt>

  const handleImportWithCountryCode = async (
    provider,
    countryCode,
    selectedTags,
    selectedFile,
    newTags
  ) => {


    try {


      // const addNewTagResponse = await dispatch(addTag(newTags)).unwrap()

      if (newTags?.length > 0) {
        const addNewTagResponse = await dispatch(addTag(newTags)).unwrap();
      }

      const selectedTagsFormatted = selectedTags?.map((tag) => ({
        tag: tag.value,
        emoji: tag.emoji,
      }));


      // ================================
      // ✅ CSV / EXCEL FLOW
      // ================================
      if (provider === "csvExcel") {
        if (!selectedFile) {
          dispatch(
            showToast({
              message: "Please select a file to import",
              type: "error",
            })
          );
          return;
        }

        const reader = new FileReader();

        reader.onload = async (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            const contacts = XLSX.utils.sheet_to_json(sheet);

            const response = await dispatch(
              saveBulkContacts({
                contacts,
                defaultCountryCode: countryCode,
                tags: selectedTagsFormatted,
              })
            ).unwrap();

            if (response.status === "success") {
              dispatch(clearContactsCache());

              dispatch(
                showToast({
                  message: "Contacts imported successfully",
                  type: "success",
                })
              );
            }
          } catch (error) {
            console.error("Error processing file:", error);
            dispatch(
              showToast({
                message:
                  error.message ||
                  "Failed to import contacts. Please check your file format.",
                type: "error",
              })
            );
          }
        };

        reader.onerror = () => {
          dispatch(
            showToast({
              message: "Failed to read file. Please try again.",
              type: "error",
            })
          );
        };

        reader.readAsArrayBuffer(selectedFile);

        return; // ⛔ IMPORTANT: stop here for csvExcel
      }

      // ================================
      // ✅ GOOGLE / ZOHO / HUBSPOT FLOW
      // ================================
      const payload = {
        defaultCountryCode: countryCode,
        tags: selectedTagsFormatted,
        category: "contact",
      };

      const response = await api.get(`/fetch-${provider}-contacts`, {
        params: payload,
      });

      if (response.data.status === "success" && response.data.url) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const messageHandler = (event) => {
          const data = event.data;

          if (data?.status === "success") {
            setTimeout(() => window.location.reload(), 2000);

            dispatch(
              showToast({
                message: "Contacts fetched successfully",
                variant: "success",
              })
            );

            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };

        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
          console.error("Popup blocked");
        }
      }
    } catch (error) {
      console.error(`${provider} Sign-In initiation failed`, error);
    }
  };

  const handleOptionClick = (opt) => {
    // if (opt.id === "csvExcel") {
    //   // Directly open file picker
    //   setTimeout(() => {
    //     if (fileInputRef.current) {
    //       fileInputRef.current.click();
    //     }
    //   }, 100);
    // } else {
    // Directly open CountryCodePrompt
    setPendingImportProvider(opt.id);
    setShowCountryCodePrompt(true);
    // }
  };


  return (
    <div
      className="page-wrapper"
      style={{ background: "white", height: "100vh" }}
    >
      <div className="content d-flex justify-content-center align-items-center">
        <div className="row">
          <div className="col-lg-12 col-md-10 col-sm-12 mx-auto">
            <h1 className="my-3">Import Contacts</h1>
            <p className="text-muted mb-2">
              Manage your contacts and data efficiently. Add the people you care
              about from social media, email, and more.
            </p>
            <p className="text-muted mb-4">
              <strong>Note:</strong> All imported entries will be saved as
              contacts only. They will not appear in the Leads section.
            </p>

            {/* <div className="mb-3">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleDownloadSample}
              >
                <i className="ti ti-download me-2"></i>
                Download Sample CSV
              </button>
            </div> */}

            <div className="import-options-list">
              {options.map((opt) => (
                <button
                  key={opt.id}
                  className="import-option"
                  onClick={() => handleOptionClick(opt)}
                >
                  <img
                    src={opt.icon}
                    alt="icon"
                    className="import-option__icon"
                  />
                  <span className="import-option__label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* File input is handled inside CountryCodePrompt for all providers */}

      <CountryCodePrompt
        visible={showCountryCodePrompt}
        onClose={() => {
          setShowCountryCodePrompt(false);
          setPendingImportProvider(null);
        }}
        onConfirm={({ countryCode, tags, file, newTags }) => {
          setShowCountryCodePrompt(false);
          if (pendingImportProvider) {
            handleImportWithCountryCode(
              pendingImportProvider,
              countryCode,
              tags,
              file,
              newTags
            );
            setPendingImportProvider(null);
          }
        }}
        provider={
          pendingImportProvider === "google"
            ? "Google"
            : pendingImportProvider === "zoho"
              ? "Zoho"
              : pendingImportProvider === "csvExcel"
                ? "csvExcel"
                : "HubSpot"
        }
      />
    </div>
  );
};

export default ImportAndExport;
